/**
 * Environment validation utilities for checking required configuration
 */

import { APP_IDS, DEFAULT_APP_ID, isKnownAppId } from '../config/apps';

interface EnvironmentCheck {
  key: string;
  name: string;
  required: boolean;
  validator?: (value: string) => boolean;
  description: string;
  /** Set for optional variables with a documented default, where "missing" is normal. */
  silentIfMissing?: boolean;
}

interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

const environmentChecks: EnvironmentCheck[] = [
  {
    key: 'VITE_OIDC_AUTHORITY',
    name: 'OIDC Authority',
    required: true,
    description: 'OIDC/OAuth2 provider authority URL (e.g., http://localhost:8084)'
  },
  {
    key: 'VITE_OIDC_CLIENT_ID',
    name: 'OIDC Client ID',
    required: true,
    description: 'OIDC/OAuth2 application client ID'
  },
  {
    key: 'VITE_API_ORGADMIN_API_BASE',
    name: 'API Base URL',
    required: true,
    description: 'Organization admin API base URL'
  },
  {
    key: 'VITE_LOGIN_REDIRECT_URL',
    name: 'Login Redirect URL',
    required: false,
    description: 'OAuth callback URL after login (defaults to /auth-callback)'
  },
  {
    key: 'VITE_LOGOUT_REDIRECT_URL',
    name: 'Logout Redirect URL',
    required: false,
    description: 'Redirect URL after logout (defaults to origin)'
  },
  {
    key: 'VITE_BRREG_ENHET_API_BASE',
    name: 'Brreg Enhet API Base URL',
    required: true,
    description: 'Base URL for Norwegian Business Registry (brreg-enhet) service'
  },
  {
    key: 'VITE_MAPBOX_ACCESS_TOKEN',
    name: 'Mapbox Access Token',
    required: false, // Not strictly required, but location features won't work
    validator: (value: string) => value.startsWith('pk.') && value.length > 50,
    description: 'Mapbox access token for location features'
  },
  {
    key: 'VITE_API_PROCUREMENT_API_BASE',
    name: 'Procurement API Base URL',
    // Optional, and silent when absent, because absence is the normal state for
    // every deployment but the reinventar one. sirk-procurement-backend is a
    // separate service on its own host; a deployment without this simply does not
    // offer procurement subscriptions, rather than failing to boot over a feature
    // it never sold (change: procurement-subscriptions).
    required: false,
    silentIfMissing: true,
    description: 'Base URL for sirk-procurement-backend; unset disables procurement subscriptions'
  },
  {
    key: 'VITE_APP_ID',
    name: 'App ID',
    required: false,
    silentIfMissing: true,
    validator: isKnownAppId,
    description: `App profile to build — one of: ${APP_IDS.join(', ')} (defaults to ${DEFAULT_APP_ID})`
  }
];

/**
 * Validates all required environment variables
 */
export const validateEnvironment = (): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  environmentChecks.forEach(check => {
    const value = import.meta.env[check.key];

    if (!value) {
      if (check.required) {
        errors.push(`Missing required environment variable: ${check.key} (${check.description})`);
      } else if (!check.silentIfMissing) {
        warnings.push(`Optional environment variable missing: ${check.key} (${check.description})`);
      }
      return;
    }

    // Run custom validator if provided
    if (check.validator && !check.validator(value)) {
      if (check.required) {
        errors.push(`Invalid ${check.name}: ${check.key} does not meet validation requirements (${check.description})`);
      } else {
        warnings.push(`Invalid ${check.name}: ${check.key} value is not recognised (${check.description})`);
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Check if Mapbox features are available
 */
export const isMapboxAvailable = (): boolean => {
  const token = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
  return !!(token && token.startsWith('pk.') && token.length > 50);
};

/**
 * Get environment-specific admin messages
 */
export const getEnvironmentMessages = (): { errors: string[], warnings: string[], info: string[] } => {
  const validation = validateEnvironment();
  const info: string[] = [];

  // Add environment-specific information
  if (import.meta.env.DEV) {
    info.push('Running in development mode');
  }

  if (import.meta.env.PROD && !isMapboxAvailable()) {
    validation.warnings.push('Location features will be limited without Mapbox access token');
  }

  return {
    errors: validation.errors,
    warnings: validation.warnings,
    info
  };
};

/**
 * Log environment validation results to console
 */
export const logEnvironmentValidation = (): void => {
  const { errors, warnings, info } = getEnvironmentMessages();

  if (errors.length > 0) {
    console.error('❌ Environment Configuration Errors:');
    errors.forEach(error => console.error(`  - ${error}`));
  }

  if (warnings.length > 0) {
    console.warn('⚠️ Environment Configuration Warnings:');
    warnings.forEach(warning => console.warn(`  - ${warning}`));
  }

  if (info.length > 0) {
    console.info('ℹ️ Environment Information:');
    info.forEach(infoMsg => console.info(`  - ${infoMsg}`));
  }

  if (errors.length === 0 && warnings.length === 0) {
    console.info('✅ Environment configuration is valid');
  }
};